// pages/messenger/messenger-job-incomplete.jsx

import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import RequestService from '../../services/requestService';
import CloseReasonService from '../../services/closeReasonService';
import { WF } from '../../components/utils/messenger-constants';
import { getStopBadge, getEmail, buildIncompleteNote } from '../../components/utils/messenger-helpers';
import RouteArrow from '../../components/common/route-arrow';

const MessengerJobIncomplete = () => {
    const navigate = useNavigate();
    const { id: encodedId, seq: encodedSeq, total: encodedTotal } = useParams();
    const id         = atob(encodedId);
    const seq        = encodedSeq   ? Number(atob(encodedSeq))   : null;
    const totalStops = encodedTotal ? Number(atob(encodedTotal)) : 1;

    const [job, setJob]                   = useState(null);
    const [reasons, setReasons]           = useState([]);
    const [step, setStep]                 = useState('reason');
    const [selectedStops, setSelectedStops] = useState(
        seq !== null ? [String(seq)] : []
    );
    const [selectAll, setSelectAll] = useState(seq === null);
    const [selected, setSelected]   = useState('');
    const [otherText, setOtherText] = useState('');
    const [note, setNote]           = useState('');
    const [loading, setLoading]     = useState(false);

    useEffect(() => {
        fetchJob();
        loadReasons();
    }, [id]);

    const fetchJob = async () => {
        try {
            const resp = await RequestService.GetRequestById(id);
            if (resp?.data?.is_success === true) {
                setJob(resp.data.result);
            }
        } catch (err) { console.error(err); }
    };

    const loadReasons = async () => {
        try {
            const resp = await CloseReasonService.SearchCloseReason({ actor_type: 2, status: 1 });
            if (resp.data.is_success && resp.data.result) {
                setReasons(resp.data.result);
            }
        } catch (err) { console.error(err); }
    };

    const details    = job?.request_details || [];
    const wfStatus   = job?.id_workflow_status;
    const incomplete = details.filter(d => d.is_complete != 1 && !d.send_end_date);

    const handleToggleAll = () => {
        if (selectAll) {
            setSelectAll(false);
            setSelectedStops([]);
        } else {
            setSelectAll(true);
            setSelectedStops(incomplete.map(d => String(d.seq)));
        }
    };

    const handleToggleStop = (seqStr) => {
        setSelectedStops(prev => {
            const next = prev.includes(seqStr)
                ? prev.filter(s => s !== seqStr)
                : [...prev, seqStr];
            setSelectAll(next.length === incomplete.length);
            return next;
        });
    };

    const handleConfirm = async () => {
        if (!selected) return;
        if (selected === 'other' && !otherText.trim()) return;

        const fullNote = buildIncompleteNote(selected, otherText, note, reasons);
        const closeReasonId = selected === 'other' ? 0 : Number(selected);

        setLoading(true);
        try {
            const targetSeqs = selectAll || selectedStops.length === incomplete.length
                ? incomplete.map(d => String(d.seq))
                : selectedStops;

            // ยิง close ทีละ task ที่เลือก
            for (const seqStr of targetSeqs) {
                const d = details.find(d => String(d.seq) === seqStr);
                if (d) {
                    await RequestService.CloseTask(id, d.id, {
                        id_close_reason: closeReasonId,
                        send_end_location: '',
                        note: fullNote,
                    });
                }
            }
            setStep('done');
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    // ── Done Screen ───────────────────────────────────────
    if (step === 'done') {
        return (
            <div className="mim-done">
                <div className="mim-done-icon">
                    <i className="mdi mdi-message-processing" />
                </div>
                <p className="mim-done-title">ส่งเหตุผลการจบงานแล้ว</p>
                <p className="mim-done-sub">
                    รายงานไปยังแอดมินแล้ว<br />
                    งานนี้จะมีสถานะเป็น 'จบงาน' หรือ อาจได้รับการมอบหมายมาอีกครั้ง
                </p>
                <div className="mim-done-footer">
                    <button
                        className="mim-btn mim-btn--primary"
                        onClick={() => navigate('/messenger/myjob', { replace: true })}
                    >
                        รับทราบ
                    </button>
                </div>
            </div>
        );
    }

    // ── Reason Screen ─────────────────────────────────────
    if (step === 'reason') {
        return (
            <div className="mim-root">
                <div className="mim-header">
                    <button className="mim-close" onClick={() => navigate(-1)}>
                        <i className="mdi mdi-arrow-left" />
                    </button>
                    <h2 className="mim-title">เหตุผลการจบงาน</h2>
                </div>

                <div className="mim-body">
                    <p className="mim-label">เลือกเหตุผล</p>
                    <div className="mim-list">
                        {reasons.map((r) => (
                            <label key={r.id} className="mim-option">
                                <input
                                    type="radio"
                                    name="reason"
                                    value={r.id}
                                    checked={selected === r.id}
                                    onChange={() => setSelected(r.id)}
                                />
                                <span>{r.name}</span>
                            </label>
                        ))}
                        <label className="mim-option">
                            <input
                                type="radio"
                                name="reason"
                                value="other"
                                checked={selected === 'other'}
                                onChange={() => setSelected('other')}
                            />
                            <span>อื่นๆ</span>
                        </label>
                    </div>

                    {selected === 'other' && (
                        <div className="mim-other" style={{ marginTop: 12 }}>
                            <label className="mim-label-note">
                                ใส่เหตุผล <span style={{ color: 'red' }}>*</span>
                            </label>
                            <textarea
                                className="mim-textarea"
                                placeholder="เกิดอุบัติเหตุระหว่างขี่รถเครื่อง"
                                value={otherText}
                                onChange={(e) => setOtherText(e.target.value)}
                                rows={4}
                            />
                        </div>
                    )}
                </div>

                {selected !== 'other' && (
                    <div className="mim-note-wrap">
                        <label className="mim-label-note">หมายเหตุ</label>
                        <textarea
                            className="mim-textarea"
                            placeholder="บอกเพิ่มเติม เช่น วางของแล้วแต่ไม่เจอคนรับ"
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            rows={3}
                        />
                    </div>
                )}

                <div className="mim-footer">
                    <button
                        className="mim-btn mim-btn--primary"
                        onClick={handleConfirm}
                        disabled={loading || !selected || (selected === 'other' && !otherText.trim())}
                    >
                        ยืนยัน
                    </button>
                    <button className="mim-btn mim-btn--ghost" onClick={() => navigate(-1)}>
                        <i className="mdi mdi-arrow-left" /> กลับไปก่อนหน้า
                    </button>
                </div>
            </div>
        );
    }

    // ── Select Stop Screen ────────────────────────────────
    return (
        <div className="mim-root">
            <div className="mim-header">
                <button className="mim-close" onClick={() => navigate(-1)}>
                    <i className="mdi mdi-arrow-left" />
                </button>
                <h2 className="mim-title">ขอจบงาน</h2>
            </div>

            <div className="mim-body">
                <p className="mim-label">เลือกจุดส่ง</p>
                <div className="mim-list mim-list--no-border">
                    <label className="mim-option">
                        <input
                            type="checkbox"
                            checked={selectAll}
                            onChange={handleToggleAll}
                        />
                        <span>ทั้งหมด</span>
                    </label>

                    {details.map((d, i) => {
                        const seqStr     = String(d.seq ?? i + 1);
                        const badge      = getStopBadge(d);
                        const isDisabled = d.is_complete == 1 || !!d.send_end_date;

                        return (
                            <div key={d.id}>
                                <label className={`mim-option ${isDisabled ? 'mim-option--disabled' : ''}`}>
                                    <input
                                        type="checkbox"
                                        checked={selectedStops.includes(seqStr)}
                                        onChange={() => !isDisabled && handleToggleStop(seqStr)}
                                        disabled={isDisabled}
                                    />
                                    <span>จุดที่ {d.seq ?? i + 1}</span>
                                </label>

                                <div className="mim-stop-card">
                                    <div className="mim-stop-card-header">
                                        <span className="mjd-label">
                                            Ref No. {job?.ref_no}{(d.seq ?? i + 1) > 1 ? `-${(d.seq ?? i + 1) - 1}` : ''}
                                        </span>
                                        <span className={`mmj-badge mmj-badge--${badge.type}`}>
                                            {badge.label}
                                        </span>
                                    </div>
                                    <div className="mim-stop-card-route">
                                        <div className="mim-stop-route-item">
                                            <span className="mjd-label">ต้นทาง</span>
                                            <span className="mjd-value">{d.origin_building_name || '-'}</span>
                                        </div>
                                        <RouteArrow />
                                        <div className="mim-stop-route-item">
                                            <span className="mjd-label">ปลายทาง</span>
                                            <span className="mjd-value">{d.destination_name || d.destination_company || '-'}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            <div className="mim-footer">
                <button
                    className="mim-btn mim-btn--primary"
                    onClick={() => setStep('reason')}
                    disabled={selectedStops.length === 0 && !selectAll}
                >
                    ยืนยัน
                </button>
                <button className="mim-btn mim-btn--ghost" onClick={() => navigate(-1)}>
                    <i className="mdi mdi-arrow-left" /> กลับไปก่อนหน้า
                </button>
            </div>
        </div>
    );
};

export default MessengerJobIncomplete;